import { useState, useEffect } from 'react';
import { itemAPI, teamAPI } from '../api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { MessageSquare, Send, Lock, Globe, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * ThreadedComments - Reusable component for comments on any item (tickets, deals, tasks, etc.)
 * 
 * @param {string} itemType - Type of item (tickets, deals, tasks, etc.)
 * @param {string} itemId - ID of the item
 * @param {boolean} showInternal - Whether to show internal/external toggle (default: true)
 * @param {number} maxHeight - Max height of scroll area in pixels (default: 400)
 */
export const ThreadedComments = ({ 
  itemType, 
  itemId, 
  showInternal = true,
  maxHeight = 400,
  onCommentAdded
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (itemId) {
      fetchComments();
    }
  }, [itemId, itemType]);

  const fetchComments = async () => {
    try {
      const res = await itemAPI.getComments(itemType, itemId, true);
      setComments(res.data || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const res = await itemAPI.addComment(itemType, itemId, {
        content: newComment,
        is_internal: isInternal
      });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
      toast.success('Comment added');
      if (onCommentAdded) onCommentAdded(res.data);
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (author) => {
    if (!author) return '?';
    if (author.full_name) {
      return author.full_name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return author.email?.[0]?.toUpperCase() || '?';
  };

  const getAuthorName = (author) => {
    if (!author) return 'Unknown';
    return author.full_name || author.email || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          <h3 className="font-semibold">Comments</h3>
          <Badge variant="secondary">{comments.length}</Badge>
        </div>
        <Button variant="ghost" size="sm">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {expanded && (
        <>
          {/* Comments List */}
          <ScrollArea className="pr-4" style={{ maxHeight }}>
            {comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment, idx) => (
                  <div 
                    key={comment.id || idx} 
                    className={`flex gap-3 ${comment.is_internal ? 'bg-amber-50 p-3 rounded-lg border border-amber-200' : ''}`}
                  >
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className={comment.is_internal ? 'bg-amber-200' : 'bg-slate-200'}>
                        {getInitials(comment.author)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{getAuthorName(comment.author)}</span>
                        <span className="text-xs text-muted-foreground">
                          {comment.created_at ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true }) : ''}
                        </span>
                        {comment.is_internal && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            <Lock className="h-3 w-3 mr-1" />
                            Internal
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm mt-1 whitespace-pre-wrap break-words">{comment.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No comments yet</p>
                <p className="text-xs">Be the first to add a comment</p>
              </div>
            )}
          </ScrollArea>

          {/* Add Comment Form */}
          <form onSubmit={handleSubmit} className="space-y-3 pt-4 border-t">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              rows={3}
              className="resize-none"
              data-testid="comment-input"
            />
            <div className="flex items-center justify-between">
              {showInternal && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="internal-comment"
                    checked={isInternal}
                    onCheckedChange={setIsInternal}
                    data-testid="internal-comment-toggle"
                  />
                  <Label htmlFor="internal-comment" className="text-sm flex items-center gap-1 cursor-pointer">
                    {isInternal ? (
                      <>
                        <Lock className="h-3 w-3" />
                        Internal note (staff only)
                      </>
                    ) : (
                      <>
                        <Globe className="h-3 w-3" />
                        Visible to customer
                      </>
                    )}
                  </Label>
                </div>
              )}
              <Button 
                type="submit" 
                size="sm" 
                disabled={!newComment.trim() || submitting}
                data-testid="submit-comment-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Add Comment
                  </>
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default ThreadedComments;
